// src/services/statementTemplates.js
// Phase 4: Comprehensive Statement Template Library for Dynamic Questions
// Covers: Linear, Quadratic, Hyperbola, Exponential

// ═══════════════════════════════════════════════════════════════════════════════
// STATEMENT CATEGORIES & TYPES
// ═══════════════════════════════════════════════════════════════════════════════

export const StatementCategory = {
  STRUCTURAL: 'structural',           // Vertex, axis of symmetry (invariants)
  ORIENTATION: 'orientation',         // Opens up/down, concavity
  INTERCEPTS: 'intercepts',           // x-intercepts, y-intercept
  DOMAIN_RANGE: 'domain_range',       // Domain, range statements
  INTERVALS: 'intervals',             // Positive/negative/increasing/decreasing
  TRANSFORMATIONS: 'transformations', // Shifts, reflections, stretches
  ASYMPTOTES: 'asymptotes',           // Vertical/horizontal asymptotes
  BEHAVIOUR: 'behaviour',             // Growth/decay, end behaviour
  INVERSES: 'inverses',               // Inverse existence, properties
  COMPARATIVE: 'comparative',         // Comparing two graphs
};

export const StatementType = {
  INVARIANT: 'invariant',             // Always true for this function type
  CONDITIONAL: 'conditional',         // Depends on parameters
  MISCONCEPTION: 'misconception',     // Designed to catch common errors
};

export const TargetSkill = {
  // Structural
  IDENTIFY_VERTEX: 'identify_vertex',
  IDENTIFY_AXIS_SYMMETRY: 'identify_axis_symmetry',
  IDENTIFY_TURNING_POINT: 'identify_turning_point',
  
  // Orientation
  INTERPRET_CONCAVITY: 'interpret_concavity',
  DETERMINE_DIRECTION: 'determine_direction',
  
  // Intercepts
  COUNT_INTERCEPTS: 'count_intercepts',
  IDENTIFY_Y_INTERCEPT: 'identify_y_intercept',
  IDENTIFY_X_INTERCEPTS: 'identify_x_intercepts',
  CALCULATE_INTERCEPTS: 'calculate_intercepts',
  
  // Domain & Range
  DETERMINE_DOMAIN: 'determine_domain',
  DETERMINE_RANGE: 'determine_range',
  IDENTIFY_EXTREMUM: 'identify_extremum',
  
  // Intervals
  IDENTIFY_SIGN_INTERVALS: 'identify_sign_intervals',
  IDENTIFY_INCREASING_INTERVAL: 'identify_increasing_interval',
  IDENTIFY_DECREASING_INTERVAL: 'identify_decreasing_interval',
  
  // Transformations
  IDENTIFY_HORIZONTAL_SHIFT: 'identify_horizontal_shift',
  IDENTIFY_VERTICAL_SHIFT: 'identify_vertical_shift',
  IDENTIFY_REFLECTION: 'identify_reflection',
  IDENTIFY_STRETCH: 'identify_stretch',
  
  // Asymptotes
  IDENTIFY_VERTICAL_ASYMPTOTE: 'identify_vertical_asymptote',
  IDENTIFY_HORIZONTAL_ASYMPTOTE: 'identify_horizontal_asymptote',
  
  // Behaviour
  IDENTIFY_GROWTH_DECAY: 'identify_growth_decay',
  IDENTIFY_END_BEHAVIOUR: 'identify_end_behaviour',
  
  // Inverses
  DETERMINE_ONE_TO_ONE: 'determine_one_to_one',
  INVERSE_EXISTS: 'inverse_exists',
};

export const Misconception = {
  CONFUSES_SIGN_OF_A: 'confuses_sign_of_a',
  CONFUSES_P_DIRECTION: 'confuses_p_direction',
  ASSUMES_POSITIVE_BETWEEN_ROOTS: 'assumes_positive_between_roots',
  CONFUSES_DOMAIN_RANGE: 'confuses_domain_range',
  CONFUSES_ASYMPTOTE_VALUE: 'confuses_asymptote_value',
  CONFUSES_GROWTH_DECAY: 'confuses_growth_decay',
  WRONG_INTERCEPT_SIGN: 'wrong_intercept_sign',
  IGNORES_DOMAIN_RESTRICTION: 'ignores_domain_restriction',
};


// ═══════════════════════════════════════════════════════════════════════════════
// LINEAR FUNCTION TEMPLATES: y = mx + c
// ═══════════════════════════════════════════════════════════════════════════════

export const LINEAR_TEMPLATES = [
  // STRUCTURAL
  {
    id: 'lin_gradient',
    category: StatementCategory.STRUCTURAL,
    type: StatementType.INVARIANT,
    text: 'The gradient of the line is {features.gradient}',
    predicate: { always: true },
    targetSkill: TargetSkill.IDENTIFY_TURNING_POINT,
    difficulty: 1,
    functionTypes: ['linear'],
  },
  
  // INTERCEPTS
  {
    id: 'lin_y_intercept_value',
    category: StatementCategory.INTERCEPTS,
    type: StatementType.INVARIANT,
    text: 'The y-intercept is {params.c}',
    predicate: { always: true },
    targetSkill: TargetSkill.IDENTIFY_Y_INTERCEPT,
    difficulty: 1,
    functionTypes: ['linear'],
  },
  {
    id: 'lin_y_intercept_positive',
    category: StatementCategory.INTERCEPTS,
    type: StatementType.CONDITIONAL,
    text: 'The y-intercept is positive',
    predicate: { gt: ['params.c', 0] },
    targetSkill: TargetSkill.IDENTIFY_Y_INTERCEPT,
    difficulty: 1,
    functionTypes: ['linear'],
  },
  {
    id: 'lin_y_intercept_negative',
    category: StatementCategory.INTERCEPTS,
    type: StatementType.CONDITIONAL,
    text: 'The y-intercept is negative',
    predicate: { lt: ['params.c', 0] },
    targetSkill: TargetSkill.IDENTIFY_Y_INTERCEPT,
    difficulty: 1,
    functionTypes: ['linear'],
  },
  {
    id: 'lin_passes_origin',
    category: StatementCategory.INTERCEPTS,
    type: StatementType.CONDITIONAL,
    text: 'The line passes through the origin',
    predicate: { eq: ['params.c', 0] },
    targetSkill: TargetSkill.IDENTIFY_Y_INTERCEPT,
    difficulty: 1,
    functionTypes: ['linear'],
  },
  {
    id: 'lin_has_x_intercept',
    category: StatementCategory.INTERCEPTS,
    type: StatementType.CONDITIONAL,
    text: 'The line has an x-intercept',
    predicate: { neq: ['params.m', 0] },
    targetSkill: TargetSkill.COUNT_INTERCEPTS,
    difficulty: 1,
    functionTypes: ['linear'],
  },
  
  // ORIENTATION / MONOTONICITY
  {
    id: 'lin_increasing',
    category: StatementCategory.INTERVALS,
    type: StatementType.CONDITIONAL,
    text: 'The function is increasing',
    predicate: { gt: ['params.m', 0] },
    targetSkill: TargetSkill.IDENTIFY_INCREASING_INTERVAL,
    difficulty: 1,
    functionTypes: ['linear'],
  },
  {
    id: 'lin_decreasing',
    category: StatementCategory.INTERVALS,
    type: StatementType.CONDITIONAL,
    text: 'The function is decreasing',
    predicate: { lt: ['params.m', 0] },
    targetSkill: TargetSkill.IDENTIFY_DECREASING_INTERVAL,
    difficulty: 1,
    functionTypes: ['linear'],
  },
  {
    id: 'lin_constant',
    category: StatementCategory.INTERVALS,
    type: StatementType.CONDITIONAL,
    text: 'The function is constant',
    predicate: { eq: ['params.m', 0] },
    targetSkill: TargetSkill.IDENTIFY_INCREASING_INTERVAL,
    difficulty: 1,
    functionTypes: ['linear'],
  },
  {
    id: 'lin_positive_gradient',
    category: StatementCategory.ORIENTATION,
    type: StatementType.CONDITIONAL,
    text: 'The gradient is positive',
    predicate: { gt: ['params.m', 0] },
    targetSkill: TargetSkill.DETERMINE_DIRECTION,
    difficulty: 1,
    functionTypes: ['linear'],
  },
  {
    id: 'lin_negative_gradient',
    category: StatementCategory.ORIENTATION,
    type: StatementType.CONDITIONAL,
    text: 'The gradient is negative',
    predicate: { lt: ['params.m', 0] },
    targetSkill: TargetSkill.DETERMINE_DIRECTION,
    difficulty: 1,
    functionTypes: ['linear'],
  },
  
  // DOMAIN & RANGE
  {
    id: 'lin_domain_all_reals',
    category: StatementCategory.DOMAIN_RANGE,
    type: StatementType.INVARIANT,
    text: 'The domain is all real numbers',
    predicate: { always: true },
    targetSkill: TargetSkill.DETERMINE_DOMAIN,
    difficulty: 1,
    functionTypes: ['linear'],
  },
  {
    id: 'lin_range_all_reals',
    category: StatementCategory.DOMAIN_RANGE,
    type: StatementType.CONDITIONAL,
    text: 'The range is all real numbers',
    predicate: { neq: ['params.m', 0] },
    targetSkill: TargetSkill.DETERMINE_RANGE,
    difficulty: 1,
    functionTypes: ['linear'],
  },
  
  // INVERSES
  {
    id: 'lin_one_to_one',
    category: StatementCategory.INVERSES,
    type: StatementType.CONDITIONAL,
    text: 'The function is one-to-one',
    predicate: { neq: ['params.m', 0] },
    targetSkill: TargetSkill.DETERMINE_ONE_TO_ONE,
    difficulty: 2,
    functionTypes: ['linear'],
  },
  {
    id: 'lin_inverse_exists',
    category: StatementCategory.INVERSES,
    type: StatementType.CONDITIONAL,
    text: 'The inverse function exists',
    predicate: { neq: ['params.m', 0] },
    targetSkill: TargetSkill.INVERSE_EXISTS,
    difficulty: 2,
    functionTypes: ['linear'],
  },
];


// ═══════════════════════════════════════════════════════════════════════════════
// QUADRATIC FUNCTION TEMPLATES: y = a(x - p)² + q
// ═══════════════════════════════════════════════════════════════════════════════

export const QUADRATIC_TEMPLATES = [
  // STRUCTURAL (Invariants)
  {
    id: 'quad_vertex',
    category: StatementCategory.STRUCTURAL,
    type: StatementType.INVARIANT,
    text: 'The vertex is at ({params.p}; {params.q})',
    predicate: { always: true },
    targetSkill: TargetSkill.IDENTIFY_VERTEX,
    difficulty: 1,
    functionTypes: ['quadratic'],
  },
  {
    id: 'quad_axis_symmetry',
    category: StatementCategory.STRUCTURAL,
    type: StatementType.INVARIANT,
    text: 'The axis of symmetry is x = {params.p}',
    predicate: { always: true },
    targetSkill: TargetSkill.IDENTIFY_AXIS_SYMMETRY,
    difficulty: 1,
    functionTypes: ['quadratic'],
  },
  {
    id: 'quad_turning_point',
    category: StatementCategory.STRUCTURAL,
    type: StatementType.INVARIANT,
    text: 'The turning point is at ({params.p}; {params.q})',
    predicate: { always: true },
    targetSkill: TargetSkill.IDENTIFY_TURNING_POINT,
    difficulty: 1,
    functionTypes: ['quadratic'],
  },
  
  // ORIENTATION
  {
    id: 'quad_opens_up',
    category: StatementCategory.ORIENTATION,
    type: StatementType.CONDITIONAL,
    text: 'The graph opens upwards',
    predicate: { gt: ['params.a', 0] },
    targetSkill: TargetSkill.INTERPRET_CONCAVITY,
    misconception: Misconception.CONFUSES_SIGN_OF_A,
    difficulty: 1,
    functionTypes: ['quadratic'],
  },
  {
    id: 'quad_opens_down',
    category: StatementCategory.ORIENTATION,
    type: StatementType.CONDITIONAL,
    text: 'The graph opens downwards',
    predicate: { lt: ['params.a', 0] },
    targetSkill: TargetSkill.INTERPRET_CONCAVITY,
    misconception: Misconception.CONFUSES_SIGN_OF_A,
    difficulty: 1,
    functionTypes: ['quadratic'],
  },
  {
    id: 'quad_concave_up',
    category: StatementCategory.ORIENTATION,
    type: StatementType.CONDITIONAL,
    text: 'The graph is concave up',
    predicate: { gt: ['params.a', 0] },
    targetSkill: TargetSkill.INTERPRET_CONCAVITY,
    difficulty: 2,
    functionTypes: ['quadratic'],
  },
  {
    id: 'quad_concave_down',
    category: StatementCategory.ORIENTATION,
    type: StatementType.CONDITIONAL,
    text: 'The graph is concave down',
    predicate: { lt: ['params.a', 0] },
    targetSkill: TargetSkill.INTERPRET_CONCAVITY,
    difficulty: 2,
    functionTypes: ['quadratic'],
  },
  
  // INTERCEPTS
  {
    id: 'quad_two_x_intercepts',
    category: StatementCategory.INTERCEPTS,
    type: StatementType.CONDITIONAL,
    text: 'The graph has two x-intercepts',
    predicate: { eq: ['features.rootCount', 2] },
    targetSkill: TargetSkill.COUNT_INTERCEPTS,
    difficulty: 2,
    functionTypes: ['quadratic'],
  },
  {
    id: 'quad_one_x_intercept',
    category: StatementCategory.INTERCEPTS,
    type: StatementType.CONDITIONAL,
    text: 'The graph touches the x-axis at exactly one point',
    predicate: { eq: ['features.rootCount', 1] },
    targetSkill: TargetSkill.COUNT_INTERCEPTS,
    difficulty: 2,
    functionTypes: ['quadratic'],
  },
  {
    id: 'quad_no_x_intercepts',
    category: StatementCategory.INTERCEPTS,
    type: StatementType.CONDITIONAL,
    text: 'The graph does not intersect the x-axis',
    predicate: { eq: ['features.rootCount', 0] },
    targetSkill: TargetSkill.COUNT_INTERCEPTS,
    difficulty: 2,
    functionTypes: ['quadratic'],
  },
  {
    id: 'quad_y_intercept_positive',
    category: StatementCategory.INTERCEPTS,
    type: StatementType.CONDITIONAL,
    text: 'The y-intercept is positive',
    predicate: { gt: ['features.yIntercept', 0] },
    targetSkill: TargetSkill.IDENTIFY_Y_INTERCEPT,
    difficulty: 2,
    functionTypes: ['quadratic'],
  },
  {
    id: 'quad_y_intercept_negative',
    category: StatementCategory.INTERCEPTS,
    type: StatementType.CONDITIONAL,
    text: 'The y-intercept is negative',
    predicate: { lt: ['features.yIntercept', 0] },
    targetSkill: TargetSkill.IDENTIFY_Y_INTERCEPT,
    difficulty: 2,
    functionTypes: ['quadratic'],
  },
  {
    id: 'quad_y_intercept_zero',
    category: StatementCategory.INTERCEPTS,
    type: StatementType.CONDITIONAL,
    text: 'The graph passes through the origin',
    predicate: { eq: ['features.yIntercept', 0] },
    targetSkill: TargetSkill.IDENTIFY_Y_INTERCEPT,
    difficulty: 2,
    functionTypes: ['quadratic'],
  },
  
  // DOMAIN & RANGE
  {
    id: 'quad_domain_all_reals',
    category: StatementCategory.DOMAIN_RANGE,
    type: StatementType.INVARIANT,
    text: 'The domain is x ∈ ℝ',
    predicate: { always: true },
    targetSkill: TargetSkill.DETERMINE_DOMAIN,
    difficulty: 1,
    functionTypes: ['quadratic'],
  },
  {
    id: 'quad_range_gte_q',
    category: StatementCategory.DOMAIN_RANGE,
    type: StatementType.CONDITIONAL,
    text: 'The range is y ≥ {params.q}',
    predicate: { gt: ['params.a', 0] },
    targetSkill: TargetSkill.DETERMINE_RANGE,
    difficulty: 2,
    functionTypes: ['quadratic'],
  },
  {
    id: 'quad_range_lte_q',
    category: StatementCategory.DOMAIN_RANGE,
    type: StatementType.CONDITIONAL,
    text: 'The range is y ≤ {params.q}',
    predicate: { lt: ['params.a', 0] },
    targetSkill: TargetSkill.DETERMINE_RANGE,
    difficulty: 2,
    functionTypes: ['quadratic'],
  },
  {
    id: 'quad_has_minimum',
    category: StatementCategory.DOMAIN_RANGE,
    type: StatementType.CONDITIONAL,
    text: 'The function has a minimum value of {params.q}',
    predicate: { gt: ['params.a', 0] },
    targetSkill: TargetSkill.IDENTIFY_EXTREMUM,
    difficulty: 2,
    functionTypes: ['quadratic'],
  },
  {
    id: 'quad_has_maximum',
    category: StatementCategory.DOMAIN_RANGE,
    type: StatementType.CONDITIONAL,
    text: 'The function has a maximum value of {params.q}',
    predicate: { lt: ['params.a', 0] },
    targetSkill: TargetSkill.IDENTIFY_EXTREMUM,
    difficulty: 2,
    functionTypes: ['quadratic'],
  },
  
  // INTERVALS - Monotonicity
  {
    id: 'quad_increasing_right_of_p',
    category: StatementCategory.INTERVALS,
    type: StatementType.CONDITIONAL,
    text: 'The function is increasing for x > {params.p}',
    predicate: { gt: ['params.a', 0] },
    targetSkill: TargetSkill.IDENTIFY_INCREASING_INTERVAL,
    difficulty: 2,
    functionTypes: ['quadratic'],
  },
  {
    id: 'quad_decreasing_right_of_p',
    category: StatementCategory.INTERVALS,
    type: StatementType.CONDITIONAL,
    text: 'The function is decreasing for x > {params.p}',
    predicate: { lt: ['params.a', 0] },
    targetSkill: TargetSkill.IDENTIFY_DECREASING_INTERVAL,
    difficulty: 2,
    functionTypes: ['quadratic'],
  },
  {
    id: 'quad_increasing_left_of_p',
    category: StatementCategory.INTERVALS,
    type: StatementType.CONDITIONAL,
    text: 'The function is increasing for x < {params.p}',
    predicate: { lt: ['params.a', 0] },
    targetSkill: TargetSkill.IDENTIFY_INCREASING_INTERVAL,
    difficulty: 2,
    functionTypes: ['quadratic'],
  },
  {
    id: 'quad_decreasing_left_of_p',
    category: StatementCategory.INTERVALS,
    type: StatementType.CONDITIONAL,
    text: 'The function is decreasing for x < {params.p}',
    predicate: { gt: ['params.a', 0] },
    targetSkill: TargetSkill.IDENTIFY_DECREASING_INTERVAL,
    difficulty: 2,
    functionTypes: ['quadratic'],
  },
  
  // INTERVALS - Sign (with misconception traps)
  {
    id: 'quad_positive_outside_roots',
    category: StatementCategory.INTERVALS,
    type: StatementType.CONDITIONAL,
    text: 'The function is positive outside its roots',
    predicate: { and: [{ gt: ['params.a', 0] }, { eq: ['features.rootCount', 2] }] },
    targetSkill: TargetSkill.IDENTIFY_SIGN_INTERVALS,
    difficulty: 3,
    functionTypes: ['quadratic'],
  },
  {
    id: 'quad_negative_outside_roots',
    category: StatementCategory.INTERVALS,
    type: StatementType.CONDITIONAL,
    text: 'The function is negative outside its roots',
    predicate: { and: [{ lt: ['params.a', 0] }, { eq: ['features.rootCount', 2] }] },
    targetSkill: TargetSkill.IDENTIFY_SIGN_INTERVALS,
    difficulty: 3,
    functionTypes: ['quadratic'],
  },
  {
    id: 'quad_positive_between_roots',
    category: StatementCategory.INTERVALS,
    type: StatementType.CONDITIONAL,
    text: 'The function is positive between its roots',
    predicate: { and: [{ lt: ['params.a', 0] }, { eq: ['features.rootCount', 2] }] },
    targetSkill: TargetSkill.IDENTIFY_SIGN_INTERVALS,
    misconception: Misconception.ASSUMES_POSITIVE_BETWEEN_ROOTS,
    difficulty: 3,
    functionTypes: ['quadratic'],
  },
  {
    id: 'quad_negative_between_roots',
    category: StatementCategory.INTERVALS,
    type: StatementType.CONDITIONAL,
    text: 'The function is negative between its roots',
    predicate: { and: [{ gt: ['params.a', 0] }, { eq: ['features.rootCount', 2] }] },
    targetSkill: TargetSkill.IDENTIFY_SIGN_INTERVALS,
    difficulty: 3,
    functionTypes: ['quadratic'],
  },
  {
    id: 'quad_always_positive',
    category: StatementCategory.INTERVALS,
    type: StatementType.CONDITIONAL,
    text: 'The function is always positive',
    predicate: { and: [{ gt: ['params.a', 0] }, { gt: ['params.q', 0] }] },
    targetSkill: TargetSkill.IDENTIFY_SIGN_INTERVALS,
    difficulty: 3,
    functionTypes: ['quadratic'],
  },
  {
    id: 'quad_always_negative',
    category: StatementCategory.INTERVALS,
    type: StatementType.CONDITIONAL,
    text: 'The function is always negative',
    predicate: { and: [{ lt: ['params.a', 0] }, { lt: ['params.q', 0] }] },
    targetSkill: TargetSkill.IDENTIFY_SIGN_INTERVALS,
    difficulty: 3,
    functionTypes: ['quadratic'],
  },
  
  // TRANSFORMATIONS
  {
    id: 'quad_shifted_right',
    category: StatementCategory.TRANSFORMATIONS,
    type: StatementType.CONDITIONAL,
    text: 'The graph is shifted {params.p} units to the right of y = ax²',
    predicate: { gt: ['params.p', 0] },
    targetSkill: TargetSkill.IDENTIFY_HORIZONTAL_SHIFT,
    misconception: Misconception.CONFUSES_P_DIRECTION,
    difficulty: 2,
    functionTypes: ['quadratic'],
  },
  {
    id: 'quad_shifted_left',
    category: StatementCategory.TRANSFORMATIONS,
    type: StatementType.CONDITIONAL,
    text: 'The graph is shifted {features.transformations.horizontalShiftAbs} units to the left of y = ax²',
    predicate: { lt: ['params.p', 0] },
    targetSkill: TargetSkill.IDENTIFY_HORIZONTAL_SHIFT,
    misconception: Misconception.CONFUSES_P_DIRECTION,
    difficulty: 2,
    functionTypes: ['quadratic'],
  },
  {
    id: 'quad_shifted_up',
    category: StatementCategory.TRANSFORMATIONS,
    type: StatementType.CONDITIONAL,
    text: 'The graph is shifted {params.q} units upwards from y = a(x - p)²',
    predicate: { gt: ['params.q', 0] },
    targetSkill: TargetSkill.IDENTIFY_VERTICAL_SHIFT,
    difficulty: 2,
    functionTypes: ['quadratic'],
  },
  {
    id: 'quad_shifted_down',
    category: StatementCategory.TRANSFORMATIONS,
    type: StatementType.CONDITIONAL,
    text: 'The graph is shifted {features.transformations.verticalShiftAbs} units downwards from y = a(x - p)²',
    predicate: { lt: ['params.q', 0] },
    targetSkill: TargetSkill.IDENTIFY_VERTICAL_SHIFT,
    difficulty: 2,
    functionTypes: ['quadratic'],
  },
  {
    id: 'quad_reflected_in_x',
    category: StatementCategory.TRANSFORMATIONS,
    type: StatementType.CONDITIONAL,
    text: 'The graph is a reflection of y = |a|(x - p)² + q in the x-axis',
    predicate: { lt: ['params.a', 0] },
    targetSkill: TargetSkill.IDENTIFY_REFLECTION,
    difficulty: 3,
    functionTypes: ['quadratic'],
  },
  {
    id: 'quad_vertically_stretched',
    category: StatementCategory.TRANSFORMATIONS,
    type: StatementType.CONDITIONAL,
    text: 'The graph is vertically stretched',
    predicate: { gt: ['features.transformations.verticalStretch', 1] },
    targetSkill: TargetSkill.IDENTIFY_STRETCH,
    difficulty: 3,
    functionTypes: ['quadratic'],
  },
  {
    id: 'quad_vertically_compressed',
    category: StatementCategory.TRANSFORMATIONS,
    type: StatementType.CONDITIONAL,
    text: 'The graph is vertically compressed',
    predicate: { and: [{ gt: ['features.transformations.verticalStretch', 0] }, { lt: ['features.transformations.verticalStretch', 1] }] },
    targetSkill: TargetSkill.IDENTIFY_STRETCH,
    difficulty: 3,
    functionTypes: ['quadratic'],
  },
  
  // INVERSES
  {
    id: 'quad_not_one_to_one',
    category: StatementCategory.INVERSES,
    type: StatementType.INVARIANT,
    text: 'The function is not one-to-one',
    predicate: { always: true },
    targetSkill: TargetSkill.DETERMINE_ONE_TO_ONE,
    difficulty: 2,
    functionTypes: ['quadratic'],
  },
  {
    id: 'quad_no_inverse_full_domain',
    category: StatementCategory.INVERSES,
    type: StatementType.INVARIANT,
    text: 'The function does not have an inverse over its full domain',
    predicate: { always: true },
    targetSkill: TargetSkill.INVERSE_EXISTS,
    difficulty: 3,
    functionTypes: ['quadratic'],
  },
];


// ═══════════════════════════════════════════════════════════════════════════════
// HYPERBOLA FUNCTION TEMPLATES: y = a/(x + p) + q
// ═══════════════════════════════════════════════════════════════════════════════

export const HYPERBOLA_TEMPLATES = [
  // ASYMPTOTES
  {
    id: 'hyp_vertical_asymptote',
    category: StatementCategory.ASYMPTOTES,
    type: StatementType.INVARIANT,
    text: 'The vertical asymptote is x = {features.verticalAsymptotes[0]}',
    predicate: { always: true },
    targetSkill: TargetSkill.IDENTIFY_VERTICAL_ASYMPTOTE,
    difficulty: 1,
    functionTypes: ['hyperbola'],
  },
  {
    id: 'hyp_horizontal_asymptote',
    category: StatementCategory.ASYMPTOTES,
    type: StatementType.INVARIANT,
    text: 'The horizontal asymptote is y = {params.q}',
    predicate: { always: true },
    targetSkill: TargetSkill.IDENTIFY_HORIZONTAL_ASYMPTOTE,
    difficulty: 1,
    functionTypes: ['hyperbola'],
  },
  {
    id: 'hyp_asymptote_intersection',
    category: StatementCategory.ASYMPTOTES,
    type: StatementType.INVARIANT,
    text: 'The asymptotes intersect at ({features.verticalAsymptotes[0]}; {params.q})',
    predicate: { always: true },
    targetSkill: TargetSkill.IDENTIFY_VERTICAL_ASYMPTOTE,
    difficulty: 2,
    functionTypes: ['hyperbola'],
  },
  
  // INTERCEPTS
  {
    id: 'hyp_has_x_intercept',
    category: StatementCategory.INTERCEPTS,
    type: StatementType.CONDITIONAL,
    text: 'The graph has an x-intercept',
    predicate: { neq: ['params.q', 0] },
    targetSkill: TargetSkill.COUNT_INTERCEPTS,
    difficulty: 2,
    functionTypes: ['hyperbola'],
  },
  {
    id: 'hyp_no_x_intercept',
    category: StatementCategory.INTERCEPTS,
    type: StatementType.CONDITIONAL,
    text: 'The graph has no x-intercept',
    predicate: { eq: ['params.q', 0] },
    targetSkill: TargetSkill.COUNT_INTERCEPTS,
    difficulty: 2,
    functionTypes: ['hyperbola'],
  },
  {
    id: 'hyp_y_intercept_exists',
    category: StatementCategory.INTERCEPTS,
    type: StatementType.CONDITIONAL,
    text: 'The graph has a y-intercept',
    predicate: { neq: ['params.p', 0] },
    targetSkill: TargetSkill.IDENTIFY_Y_INTERCEPT,
    difficulty: 2,
    functionTypes: ['hyperbola'],
  },
  {
    id: 'hyp_no_y_intercept',
    category: StatementCategory.INTERCEPTS,
    type: StatementType.CONDITIONAL,
    text: 'The graph has no y-intercept',
    predicate: { eq: ['params.p', 0] },
    targetSkill: TargetSkill.IDENTIFY_Y_INTERCEPT,
    difficulty: 2,
    functionTypes: ['hyperbola'],
  },
  
  // DOMAIN & RANGE
  {
    id: 'hyp_domain_excludes_asymptote',
    category: StatementCategory.DOMAIN_RANGE,
    type: StatementType.INVARIANT,
    text: 'The domain is x ∈ ℝ, x ≠ {features.verticalAsymptotes[0]}',
    predicate: { always: true },
    targetSkill: TargetSkill.DETERMINE_DOMAIN,
    difficulty: 2,
    functionTypes: ['hyperbola'],
  },
  {
    id: 'hyp_range_excludes_asymptote',
    category: StatementCategory.DOMAIN_RANGE,
    type: StatementType.INVARIANT,
    text: 'The range is y ∈ ℝ, y ≠ {params.q}',
    predicate: { always: true },
    targetSkill: TargetSkill.DETERMINE_RANGE,
    difficulty: 2,
    functionTypes: ['hyperbola'],
  },
  
  // ORIENTATION / QUADRANTS
  {
    id: 'hyp_quadrants_1_3',
    category: StatementCategory.ORIENTATION,
    type: StatementType.CONDITIONAL,
    text: 'The graph lies in quadrants 1 and 3 (relative to the asymptotes)',
    predicate: { gt: ['params.a', 0] },
    targetSkill: TargetSkill.DETERMINE_DIRECTION,
    difficulty: 2,
    functionTypes: ['hyperbola'],
  },
  {
    id: 'hyp_quadrants_2_4',
    category: StatementCategory.ORIENTATION,
    type: StatementType.CONDITIONAL,
    text: 'The graph lies in quadrants 2 and 4 (relative to the asymptotes)',
    predicate: { lt: ['params.a', 0] },
    targetSkill: TargetSkill.DETERMINE_DIRECTION,
    difficulty: 2,
    functionTypes: ['hyperbola'],
  },
  
  // INTERVALS - Monotonicity
  {
    id: 'hyp_decreasing_both_branches',
    category: StatementCategory.INTERVALS,
    type: StatementType.CONDITIONAL,
    text: 'The function is decreasing on both branches',
    predicate: { gt: ['params.a', 0] },
    targetSkill: TargetSkill.IDENTIFY_DECREASING_INTERVAL,
    difficulty: 3,
    functionTypes: ['hyperbola'],
  },
  {
    id: 'hyp_increasing_both_branches',
    category: StatementCategory.INTERVALS,
    type: StatementType.CONDITIONAL,
    text: 'The function is increasing on both branches',
    predicate: { lt: ['params.a', 0] },
    targetSkill: TargetSkill.IDENTIFY_INCREASING_INTERVAL,
    difficulty: 3,
    functionTypes: ['hyperbola'],
  },
  
  // TRANSFORMATIONS
  {
    id: 'hyp_shifted_left',
    category: StatementCategory.TRANSFORMATIONS,
    type: StatementType.CONDITIONAL,
    text: 'The graph is shifted {params.p} units to the left of y = a/x',
    predicate: { gt: ['params.p', 0] },
    targetSkill: TargetSkill.IDENTIFY_HORIZONTAL_SHIFT,
    difficulty: 2,
    functionTypes: ['hyperbola'],
  },
  {
    id: 'hyp_shifted_right',
    category: StatementCategory.TRANSFORMATIONS,
    type: StatementType.CONDITIONAL,
    text: 'The graph is shifted {features.transformations.horizontalShiftAbs} units to the right of y = a/x',
    predicate: { lt: ['params.p', 0] },
    targetSkill: TargetSkill.IDENTIFY_HORIZONTAL_SHIFT,
    difficulty: 2,
    functionTypes: ['hyperbola'],
  },
  {
    id: 'hyp_shifted_up',
    category: StatementCategory.TRANSFORMATIONS,
    type: StatementType.CONDITIONAL,
    text: 'The graph is shifted {params.q} units upwards from y = a/(x + p)',
    predicate: { gt: ['params.q', 0] },
    targetSkill: TargetSkill.IDENTIFY_VERTICAL_SHIFT,
    difficulty: 2,
    functionTypes: ['hyperbola'],
  },
  {
    id: 'hyp_shifted_down',
    category: StatementCategory.TRANSFORMATIONS,
    type: StatementType.CONDITIONAL,
    text: 'The graph is shifted {features.transformations.verticalShiftAbs} units downwards from y = a/(x + p)',
    predicate: { lt: ['params.q', 0] },
    targetSkill: TargetSkill.IDENTIFY_VERTICAL_SHIFT,
    difficulty: 2,
    functionTypes: ['hyperbola'],
  },
  
  // INVERSES
  {
    id: 'hyp_one_to_one',
    category: StatementCategory.INVERSES,
    type: StatementType.INVARIANT,
    text: 'The function is one-to-one',
    predicate: { always: true },
    targetSkill: TargetSkill.DETERMINE_ONE_TO_ONE,
    difficulty: 2,
    functionTypes: ['hyperbola'],
  },
  {
    id: 'hyp_inverse_exists',
    category: StatementCategory.INVERSES,
    type: StatementType.INVARIANT,
    text: 'The inverse function exists',
    predicate: { always: true },
    targetSkill: TargetSkill.INVERSE_EXISTS,
    difficulty: 2,
    functionTypes: ['hyperbola'],
  },
];


// ═══════════════════════════════════════════════════════════════════════════════
// EXPONENTIAL FUNCTION TEMPLATES: y = a·b^(x+p) + q
// ═══════════════════════════════════════════════════════════════════════════════

export const EXPONENTIAL_TEMPLATES = [
  // ASYMPTOTES
  {
    id: 'exp_horizontal_asymptote',
    category: StatementCategory.ASYMPTOTES,
    type: StatementType.INVARIANT,
    text: 'The horizontal asymptote is y = {params.q}',
    predicate: { always: true },
    targetSkill: TargetSkill.IDENTIFY_HORIZONTAL_ASYMPTOTE,
    difficulty: 1,
    functionTypes: ['exponential'],
  },
  {
    id: 'exp_no_vertical_asymptote',
    category: StatementCategory.ASYMPTOTES,
    type: StatementType.INVARIANT,
    text: 'The graph has no vertical asymptote',
    predicate: { always: true },
    targetSkill: TargetSkill.IDENTIFY_VERTICAL_ASYMPTOTE,
    difficulty: 1,
    functionTypes: ['exponential'],
  },
  
  // BEHAVIOUR
  {
    id: 'exp_growth',
    category: StatementCategory.BEHAVIOUR,
    type: StatementType.CONDITIONAL,
    text: 'The function represents exponential growth',
    predicate: { and: [{ gt: ['params.a', 0] }, { gt: ['params.b', 1] }] },
    targetSkill: TargetSkill.IDENTIFY_GROWTH_DECAY,
    misconception: Misconception.CONFUSES_GROWTH_DECAY,
    difficulty: 2,
    functionTypes: ['exponential'],
  },
  {
    id: 'exp_decay',
    category: StatementCategory.BEHAVIOUR,
    type: StatementType.CONDITIONAL,
    text: 'The function represents exponential decay',
    predicate: { and: [{ gt: ['params.a', 0] }, { lt: ['params.b', 1] }] },
    targetSkill: TargetSkill.IDENTIFY_GROWTH_DECAY,
    misconception: Misconception.CONFUSES_GROWTH_DECAY,
    difficulty: 2,
    functionTypes: ['exponential'],
  },
  {
    id: 'exp_base_greater_than_1',
    category: StatementCategory.BEHAVIOUR,
    type: StatementType.CONDITIONAL,
    text: 'The base is greater than 1',
    predicate: { gt: ['params.b', 1] },
    targetSkill: TargetSkill.IDENTIFY_GROWTH_DECAY,
    difficulty: 1,
    functionTypes: ['exponential'],
  },
  {
    id: 'exp_base_between_0_and_1',
    category: StatementCategory.BEHAVIOUR,
    type: StatementType.CONDITIONAL,
    text: 'The base is between 0 and 1',
    predicate: { and: [{ gt: ['params.b', 0] }, { lt: ['params.b', 1] }] },
    targetSkill: TargetSkill.IDENTIFY_GROWTH_DECAY,
    difficulty: 1,
    functionTypes: ['exponential'],
  },
  
  // INTERCEPTS
  {
    id: 'exp_has_x_intercept',
    category: StatementCategory.INTERCEPTS,
    type: StatementType.CONDITIONAL,
    text: 'The graph has an x-intercept',
    predicate: { gt: ['features.rootCount', 0] },
    targetSkill: TargetSkill.COUNT_INTERCEPTS,
    difficulty: 2,
    functionTypes: ['exponential'],
  },
  {
    id: 'exp_no_x_intercept',
    category: StatementCategory.INTERCEPTS,
    type: StatementType.CONDITIONAL,
    text: 'The graph has no x-intercept',
    predicate: { eq: ['features.rootCount', 0] },
    targetSkill: TargetSkill.COUNT_INTERCEPTS,
    difficulty: 2,
    functionTypes: ['exponential'],
  },
  {
    id: 'exp_y_intercept_positive',
    category: StatementCategory.INTERCEPTS,
    type: StatementType.CONDITIONAL,
    text: 'The y-intercept is positive',
    predicate: { gt: ['features.yIntercept', 0] },
    targetSkill: TargetSkill.IDENTIFY_Y_INTERCEPT,
    difficulty: 2,
    functionTypes: ['exponential'],
  },
  {
    id: 'exp_y_intercept_negative',
    category: StatementCategory.INTERCEPTS,
    type: StatementType.CONDITIONAL,
    text: 'The y-intercept is negative',
    predicate: { lt: ['features.yIntercept', 0] },
    targetSkill: TargetSkill.IDENTIFY_Y_INTERCEPT,
    difficulty: 2,
    functionTypes: ['exponential'],
  },
  
  // DOMAIN & RANGE
  {
    id: 'exp_domain_all_reals',
    category: StatementCategory.DOMAIN_RANGE,
    type: StatementType.INVARIANT,
    text: 'The domain is x ∈ ℝ',
    predicate: { always: true },
    targetSkill: TargetSkill.DETERMINE_DOMAIN,
    difficulty: 1,
    functionTypes: ['exponential'],
  },
  {
    id: 'exp_range_above_asymptote',
    category: StatementCategory.DOMAIN_RANGE,
    type: StatementType.CONDITIONAL,
    text: 'The range is y > {params.q}',
    predicate: { gt: ['params.a', 0] },
    targetSkill: TargetSkill.DETERMINE_RANGE,
    difficulty: 2,
    functionTypes: ['exponential'],
  },
  {
    id: 'exp_range_below_asymptote',
    category: StatementCategory.DOMAIN_RANGE,
    type: StatementType.CONDITIONAL,
    text: 'The range is y < {params.q}',
    predicate: { lt: ['params.a', 0] },
    targetSkill: TargetSkill.DETERMINE_RANGE,
    difficulty: 2,
    functionTypes: ['exponential'],
  },
  
  // INTERVALS - Monotonicity
  {
    id: 'exp_always_increasing',
    category: StatementCategory.INTERVALS,
    type: StatementType.CONDITIONAL,
    text: 'The function is always increasing',
    predicate: { or: [
      { and: [{ gt: ['params.a', 0] }, { gt: ['params.b', 1] }] },
      { and: [{ lt: ['params.a', 0] }, { lt: ['params.b', 1] }] }
    ]},
    targetSkill: TargetSkill.IDENTIFY_INCREASING_INTERVAL,
    difficulty: 3,
    functionTypes: ['exponential'],
  },
  {
    id: 'exp_always_decreasing',
    category: StatementCategory.INTERVALS,
    type: StatementType.CONDITIONAL,
    text: 'The function is always decreasing',
    predicate: { or: [
      { and: [{ gt: ['params.a', 0] }, { lt: ['params.b', 1] }] },
      { and: [{ lt: ['params.a', 0] }, { gt: ['params.b', 1] }] }
    ]},
    targetSkill: TargetSkill.IDENTIFY_DECREASING_INTERVAL,
    difficulty: 3,
    functionTypes: ['exponential'],
  },
  
  // INTERVALS - Sign
  {
    id: 'exp_always_positive',
    category: StatementCategory.INTERVALS,
    type: StatementType.CONDITIONAL,
    text: 'The function is always positive',
    predicate: { and: [{ gt: ['params.a', 0] }, { gte: ['params.q', 0] }] },
    targetSkill: TargetSkill.IDENTIFY_SIGN_INTERVALS,
    difficulty: 2,
    functionTypes: ['exponential'],
  },
  {
    id: 'exp_always_negative',
    category: StatementCategory.INTERVALS,
    type: StatementType.CONDITIONAL,
    text: 'The function is always negative',
    predicate: { and: [{ lt: ['params.a', 0] }, { lte: ['params.q', 0] }] },
    targetSkill: TargetSkill.IDENTIFY_SIGN_INTERVALS,
    difficulty: 2,
    functionTypes: ['exponential'],
  },
  {
    id: 'exp_above_asymptote',
    category: StatementCategory.INTERVALS,
    type: StatementType.CONDITIONAL,
    text: 'The graph lies above the asymptote',
    predicate: { gt: ['params.a', 0] },
    targetSkill: TargetSkill.IDENTIFY_SIGN_INTERVALS,
    difficulty: 2,
    functionTypes: ['exponential'],
  },
  {
    id: 'exp_below_asymptote',
    category: StatementCategory.INTERVALS,
    type: StatementType.CONDITIONAL,
    text: 'The graph lies below the asymptote',
    predicate: { lt: ['params.a', 0] },
    targetSkill: TargetSkill.IDENTIFY_SIGN_INTERVALS,
    difficulty: 2,
    functionTypes: ['exponential'],
  },
  
  // TRANSFORMATIONS
  {
    id: 'exp_shifted_left',
    category: StatementCategory.TRANSFORMATIONS,
    type: StatementType.CONDITIONAL,
    text: 'The graph is shifted {params.p} units to the left of y = a·bˣ',
    predicate: { gt: ['params.p', 0] },
    targetSkill: TargetSkill.IDENTIFY_HORIZONTAL_SHIFT,
    difficulty: 2,
    functionTypes: ['exponential'],
  },
  {
    id: 'exp_shifted_right',
    category: StatementCategory.TRANSFORMATIONS,
    type: StatementType.CONDITIONAL,
    text: 'The graph is shifted {features.transformations.horizontalShiftAbs} units to the right of y = a·bˣ',
    predicate: { lt: ['params.p', 0] },
    targetSkill: TargetSkill.IDENTIFY_HORIZONTAL_SHIFT,
    difficulty: 2,
    functionTypes: ['exponential'],
  },
  {
    id: 'exp_shifted_up',
    category: StatementCategory.TRANSFORMATIONS,
    type: StatementType.CONDITIONAL,
    text: 'The graph is shifted {params.q} units upwards',
    predicate: { gt: ['params.q', 0] },
    targetSkill: TargetSkill.IDENTIFY_VERTICAL_SHIFT,
    difficulty: 2,
    functionTypes: ['exponential'],
  },
  {
    id: 'exp_shifted_down',
    category: StatementCategory.TRANSFORMATIONS,
    type: StatementType.CONDITIONAL,
    text: 'The graph is shifted {features.transformations.verticalShiftAbs} units downwards',
    predicate: { lt: ['params.q', 0] },
    targetSkill: TargetSkill.IDENTIFY_VERTICAL_SHIFT,
    difficulty: 2,
    functionTypes: ['exponential'],
  },
  {
    id: 'exp_reflected_in_x',
    category: StatementCategory.TRANSFORMATIONS,
    type: StatementType.CONDITIONAL,
    text: 'The graph is reflected in the x-axis',
    predicate: { lt: ['params.a', 0] },
    targetSkill: TargetSkill.IDENTIFY_REFLECTION,
    difficulty: 2,
    functionTypes: ['exponential'],
  },
  
  // INVERSES
  {
    id: 'exp_one_to_one',
    category: StatementCategory.INVERSES,
    type: StatementType.INVARIANT,
    text: 'The function is one-to-one',
    predicate: { always: true },
    targetSkill: TargetSkill.DETERMINE_ONE_TO_ONE,
    difficulty: 2,
    functionTypes: ['exponential'],
  },
  {
    id: 'exp_inverse_exists',
    category: StatementCategory.INVERSES,
    type: StatementType.INVARIANT,
    text: 'The inverse function exists',
    predicate: { always: true },
    targetSkill: TargetSkill.INVERSE_EXISTS,
    difficulty: 2,
    functionTypes: ['exponential'],
  },
  {
    id: 'exp_inverse_is_log',
    category: StatementCategory.INVERSES,
    type: StatementType.INVARIANT,
    text: 'The inverse function is a logarithmic function',
    predicate: { always: true },
    targetSkill: TargetSkill.INVERSE_EXISTS,
    difficulty: 3,
    functionTypes: ['exponential'],
  },
];


// ═══════════════════════════════════════════════════════════════════════════════
// COMBINED TEMPLATE REGISTRY
// ═══════════════════════════════════════════════════════════════════════════════

export const ALL_TEMPLATES = [
  ...LINEAR_TEMPLATES,
  ...QUADRATIC_TEMPLATES,
  ...HYPERBOLA_TEMPLATES,
  ...EXPONENTIAL_TEMPLATES,
];

/**
 * Get templates for a specific function type
 */
export function getTemplatesForFunction(functionType) {
  return ALL_TEMPLATES.filter(t => 
    t.functionTypes.includes(functionType) || t.functionTypes.includes('all')
  );
}

/**
 * Get templates by category
 */
export function getTemplatesByCategory(category, functionType = null) {
  let templates = ALL_TEMPLATES.filter(t => t.category === category);
  if (functionType) {
    templates = templates.filter(t => 
      t.functionTypes.includes(functionType) || t.functionTypes.includes('all')
    );
  }
  return templates;
}

/**
 * Get templates by difficulty range
 */
export function getTemplatesByDifficulty(minDifficulty, maxDifficulty, functionType = null) {
  let templates = ALL_TEMPLATES.filter(t => 
    t.difficulty >= minDifficulty && t.difficulty <= maxDifficulty
  );
  if (functionType) {
    templates = templates.filter(t => 
      t.functionTypes.includes(functionType) || t.functionTypes.includes('all')
    );
  }
  return templates;
}

/**
 * Get templates that target a specific skill
 */
export function getTemplatesBySkill(targetSkill, functionType = null) {
  let templates = ALL_TEMPLATES.filter(t => t.targetSkill === targetSkill);
  if (functionType) {
    templates = templates.filter(t => 
      t.functionTypes.includes(functionType) || t.functionTypes.includes('all')
    );
  }
  return templates;
}

/**
 * Get misconception templates (for diagnostic assessment)
 */
export function getMisconceptionTemplates(functionType = null) {
  let templates = ALL_TEMPLATES.filter(t => t.misconception);
  if (functionType) {
    templates = templates.filter(t => 
      t.functionTypes.includes(functionType) || t.functionTypes.includes('all')
    );
  }
  return templates;
}

export default {
  StatementCategory,
  StatementType,
  TargetSkill,
  Misconception,
  LINEAR_TEMPLATES,
  QUADRATIC_TEMPLATES,
  HYPERBOLA_TEMPLATES,
  EXPONENTIAL_TEMPLATES,
  ALL_TEMPLATES,
  getTemplatesForFunction,
  getTemplatesByCategory,
  getTemplatesByDifficulty,
  getTemplatesBySkill,
  getMisconceptionTemplates,
};